import { useMemo } from "react";
import { getConsumptionProjectionChartData } from "./consumptionProjectionUtils";
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import Plot, { LocalisedPlotProps } from "components/plotly/LocalisedPlot";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useMaterialDisplayName } from "hooks/useMaterials";
import { StrategyDashboardConsumptionProjectionsData } from "src/store/api/generatedApi";
import { ConsumptionDisplayMode } from "./consumptionTypes";
import { useFormatNumber } from "src/utils/formatNumber";
import { sorted } from "src/helpers";

type ConsumptionProjectionChartProps = {
  data: StrategyDashboardConsumptionProjectionsData | undefined;
  isLoading: boolean;
  displayMode: ConsumptionDisplayMode;
} & Omit<LocalisedPlotProps, "data">;

export const ConsumptionProjectionChart = ({
  data,
  isLoading,
  displayMode,
  layout,
  ...plotProps
}: ConsumptionProjectionChartProps) => {
  const { t } = useTenantTranslation();
  const formatNumber = useFormatNumber();
  const units = useUnitsFormatter(false);
  const materialDisplayName = useMaterialDisplayName();

  const theme = useTheme();

  const plotData: Plotly.Data[] = useMemo(() => {
    const chartData = getConsumptionProjectionChartData(data);
    if (chartData.length === 0) return [];
    const formatMass = formatNumber(0);
    const massSuffix = units("mass");
    const barLabel = (fraction: number, mass: number) =>
      displayMode === "percentage"
        ? `${formatNumber(1, true)(fraction * 100)}%`
        : `${formatMass(mass)}${massSuffix}`;
    const barValue = (fraction: number, mass: number) =>
      displayMode === "percentage" ? fraction : mass;
    const sortedData = sorted(
      chartData,
      (d) =>
        displayMode === "percentage"
          ? Math.max(d.projectionFraction, d.trajectoryFraction)
          : Math.max(d.projectionMass, d.trajectoryMass),
      true
    );
    return [
      {
        y: sortedData.map((d) => materialDisplayName(d.material_name)),
        x: sortedData.map((d) =>
          barValue(d.projectionFraction, d.projectionMass)
        ),
        name: t("plannedAfterBuying"),
        type: "bar",
        orientation: "h",
        text: sortedData.map((d) =>
          barLabel(d.projectionFraction, d.projectionMass)
        ),
        textposition: "outside",
        marker: {
          color: theme.palette.primary.main,
        },
      },
      {
        y: sortedData.map((d) => materialDisplayName(d.material_name)),
        x: sortedData.map((d) =>
          barValue(d.trajectoryFraction, d.trajectoryMass)
        ),
        name: t("eomProjected"),
        type: "bar",
        orientation: "h",
        text: sortedData.map((d) =>
          barLabel(d.trajectoryFraction, d.trajectoryMass)
        ),
        textposition: "outside",
        marker: {
          color: theme.palette.data.lightblue,
        },
      },
    ];
  }, [data, t, theme, displayMode, formatNumber, units, materialDisplayName]);

  const layoutWithTickFormat = useMemo(
    () => ({
      ...layout,
      xaxis: {
        ...layout.xaxis,
        tickformat: displayMode === "percentage" ? ",.0%" : ",.0f",
      },
    }),
    [layout, displayMode]
  );

  return plotData.length > 0 ? (
    <Box sx={{ position: "relative" }}>
      <Plot {...plotProps} layout={layoutWithTickFormat} data={plotData} />
      <ChartLoadingOverlay isLoading={isLoading} />
    </Box>
  ) : (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
      }}
    >
      <Typography variant="body1" sx={{ color: "text.secondary" }}>
        {t(isLoading ? "loading" : "noData")}
      </Typography>
    </Box>
  );
};

const ChartLoadingOverlay = ({ isLoading }: { isLoading: boolean }) => {
  if (!isLoading) return null;
  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10,
        borderRadius: 1,
      }}
    >
      <Stack spacing={2} alignItems="center">
        <CircularProgress size={40} />
      </Stack>
    </Box>
  );
};
